// Which streamed `part` events the consultant client should KEEP.
//
// Extracted from ConsultantContext's SSE reader so the rule is unit-testable
// in isolation — it was previously an inline `if (part.type === "text") break;`
// that silently dropped every provenance chip during live streaming.
//
// Background: the backend emits a `part` event for each finalized MessagePart
// (see `emit("part", { part })` in supabase/functions/cattle-consultant).
// Prose ALSO arrives incrementally via "text" delta events, so keeping the
// finalized prose block as well would render the answer twice. Hence the skip.
//
// The trap: citation-only parts are also `type: "text"`. Tools that synthesize
// prose from a JSON payload (rather than emitting a table) attach their source
// as an EMPTY-content text part carrying `provenance`. Those have no prose to
// duplicate. The blanket skip threw them away, so provenance chips never
// appeared in a live conversation — while the same parts persisted fine to
// consultant_messages and rendered correctly after a page reload, because the
// reload path reads stored content instead of the stream.

import type { MessagePart } from "@/types/consultant";

/**
 * True when a streamed text part exists ONLY to carry source attribution:
 * no prose content, but a provenance record attached.
 */
export function isCitationOnlyPart(part: MessagePart): boolean {
  if (part.type !== "text") return false;
  // `content` is typed as string but arrives off the wire — treat null /
  // undefined / "" alike. Whitespace-only counts as empty too: a part holding
  // just "\n" is not prose worth rendering, and if it carries provenance we
  // still want the chip.
  const hasProse = typeof part.content === "string" && part.content.trim() !== "";
  return !hasProse && part.provenance != null;
}

/**
 * True when the consultant client should append a streamed part to the message.
 *
 * Non-text parts (table, chart, suggestions, …) are always kept — they have no
 * delta channel. Text parts are kept ONLY when they are citation-only.
 */
export function shouldKeepStreamedPart(part: MessagePart | undefined | null): boolean {
  if (!part) return false;
  if (part.type !== "text") return true;
  return isCitationOnlyPart(part);
}

/**
 * Append a streaming text delta to a parts array, returning a NEW array.
 *
 * Deltas coalesce into the trailing text part so "text → tool → more text"
 * produces separate blocks naturally. Two rules this must not break:
 *
 *  1. NEVER merge into a citation-only part. Those exist solely to carry
 *     provenance; absorbing prose into one turns a source chip into a prose
 *     block and buries the attribution. Start a fresh part instead.
 *  2. PRESERVE the rest of the part when merging. The original implementation
 *     rebuilt the trailing part as `{ type, content }`, which dropped
 *     `provenance` from any prose part that carried it. Spread instead.
 *
 * Rule 1 exists because it silently defeats the citation fix: rescue the
 * citation part from the stream, then let the next delta merge into it, and the
 * provenance is gone again with no visible symptom.
 */
export function appendTextDelta(content: MessagePart[], delta: string): MessagePart[] {
  const next = [...content];
  const lastIdx = next.length - 1;
  const last = lastIdx >= 0 ? next[lastIdx] : undefined;

  const canMerge = !!last && last.type === "text" && !isCitationOnlyPart(last);
  if (canMerge) {
    const lastText = last as Extract<MessagePart, { type: "text" }>;
    next[lastIdx] = { ...lastText, content: (lastText.content ?? "") + delta };
  } else {
    next.push({ type: "text", content: delta });
  }
  return next;
}

/**
 * Move the follow-up suggestions to the end of a message.
 *
 * ⚠️ THE MODEL DECIDES WHEN TO CALL emit_suggestions, AND IT GETS IT WRONG.
 * The system prompt says to call it "at the end of EVERY response" (rule 9),
 * but the model sometimes calls it partway through and then keeps writing — so
 * the chips render mid-answer with prose continuing underneath them, which the
 * owner saw on the second live question of the boxed-beef record tools.
 *
 * ⚠️ FIXED IN THE RENDERER, NOT IN THE PROMPT, ON PURPOSE. Asking the model
 * more firmly is unenforceable and a prompt edit is the highest-risk change in
 * this codebase — required review plus a paid eval — to fix what is really a
 * display-order problem. Sorting here cannot be violated by model behaviour,
 * which is the same reasoning behind get_boxed_beef_stat returning one metric
 * rather than trusting the model to mention one.
 *
 * Order within each group is preserved, so prose and tables keep their
 * sequence and only the suggestions move.
 */
export function withSuggestionsLast<T extends MessagePart>(parts: T[]): T[] {
  if (parts.length < 2) return parts;
  const suggestions = parts.filter(p => p.type === "suggestions");
  if (suggestions.length === 0) return parts;
  return [...parts.filter(p => p.type !== "suggestions"), ...suggestions];
}
